import React, { FC, useEffect, useRef, useState } from 'react';
import Stack from '@mui/material/Stack';
import {
  TwAccordion,
  TwAccordionDetails,
  TwAccordionSummary
} from '../../../components/TwAccordion/TwAccordion';
import Typography from '@mui/material/Typography';
import { DragDropContext, DragUpdate, DropResult, Droppable } from 'react-beautiful-dnd';
import TwOnlyinfoStop from '../../../components/TwOnlyInfoStop/TwOnlyInfoStop';
import TwStop, { TwStopHandle } from '../../../components/TwStop/TwStop';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import useAuth from '../../../providers/Auth/Auth.provider';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { LOAD_TYPE } from '../../../redux/api/load/load.model';
import { TwAutocompleteOption } from '../../../components/TwAutocomplete/TwAutocomplete';
import DateUtils from '../../../utils/dateUtils';
import useTwLocation from '../../../hooks/useTwLocation';

import {
  SliceTrip,
  addSegment,
  reorderStopInSegment
} from '../../../redux/features/orders/ordersSlice';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import { EntityType } from '../../../redux/Global.model';

interface TripRouteProps {
  segment: SliceTrip;
  segmentIndex: number;
  isImport?: boolean;
  fullScreenImport?: boolean;
  createOrder?: boolean;
  hasLocationBeenUpdated: boolean;
  locations: TwAutocompleteOption[];
  blockFetchLocations: boolean;
  tripToDelete: (id: string) => void;
  handleLocationEditOpen: (open: boolean) => void;
  handleSelectedLocationId: (id: string) => void;
  handleCreateLocationOpen: (open: boolean) => void;
  handleOpenDeleteTripConfirmation: (open: boolean) => void;
  handleActiveStopUiId: (uiId: string | null) => void;
  refetchLocations: (name?: string) => void;
}

const TripsRoute: FC<TripRouteProps> = ({
  segment,
  segmentIndex,
  isImport = false,
  fullScreenImport,
  createOrder,
  hasLocationBeenUpdated,
  locations,
  blockFetchLocations,
  tripToDelete,
  handleLocationEditOpen,
  handleSelectedLocationId,
  handleCreateLocationOpen,
  handleOpenDeleteTripConfirmation,
  handleActiveStopUiId,
  refetchLocations
}): JSX.Element => {
  const { data: user } = useAuth();
  const dispatch = useAppDispatch();
  const { addNotification } = useNotifications();
  const loadState = useAppSelector((state) => state.orders);

  const use24HourClock =
    (user.userData?.tenantSettings && user.userData.tenantSettings['use-24-hour-clock']) || false;

  const isSplit = loadState.order_type === LOAD_TYPE.SEGMENT;
  const segments = loadState.trips;
  const stops = loadState.provider_stops;

  const stopsRef = useRef<Array<TwStopHandle | null>>([]);

  const [times, setTimes] = useState<TwAutocompleteOption[]>([]);
  const [enabled, setEnabled] = useState<boolean>(false);
  // Locations

  const TIMES = !use24HourClock
    ? DateUtils.getDayTimesByInterval(15)
    : DateUtils.getDayTimesByInterval(15, { hourCycle: 'h23' });

  const handleStopLocationInputChange = (value: string | null, order: number): void => {
    const hasSelectedValueChanged = stops[order - 1]?.provider_location?.label !== value;
    hasSelectedValueChanged && !blockFetchLocations && refetchLocations(value ?? undefined);
  };

  const handleStopLocationSelected = (): void => {
    refetchLocations();
  };

  const handleOpenCreateLocation = (stopUiId: string): void => {
    handleActiveStopUiId(stopUiId);
    handleCreateLocationOpen(true);
  };

  const handleOpenEditLocation = (locationId: string, stopUiId: string): void => {
    handleSelectedLocationId(locationId);
    handleActiveStopUiId(stopUiId);
    handleLocationEditOpen(true);
  };

  const addSegmentToRoute = (): void => {
    dispatch(addSegment());
  };

  const onDragEnd = async (result: DropResult): Promise<void> => {
    const { destination, source } = result;
    if (!destination) {
      addNotification({
        variant: 'error',
        message: 'Invalid destination: Stops cannot be moved outside its trip.'
      });
      return;
    }

    dispatch(
      reorderStopInSegment({
        tripId: segment.ui_id,
        originalIndex: source.index,
        newIndex: destination.index
      })
    );
  };

  /**
   * Use effect to make drag n drop work with StrictMode
   * See link for more info: https://github.com/atlassian/react-beautiful-dnd/issues/2399#issuecomment-1175638194
   */

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));

    return (): void => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  useEffect(() => {
    refetchLocations();

    const timeOptions: TwAutocompleteOption[] = TIMES.map((t) => ({
      id: t,
      label: t,
      value: t
    }));

    setTimes(timeOptions);
  }, []);

  // #endregion

  return (
    <>
      <TwAccordion defaultExpanded={true} key={segment.id} sx={{ backgroundColor: '#F3F4F6' }}>
        <TwAccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack
            flexDirection={'row'}
            justifyContent={'space-between'}
            alignItems={'center'}
            width={'100%'}
          >
            <Typography
              sx={{
                fontSize: '12px',
                weight: '500',
                color: '#6B7280',
                marginLeft: '10px'
              }}
            >
              {isSplit ? `TRIP ${segment.order}` : 'ROUTE'}
            </Typography>
            {segments.length > 1 && (
              <CloseIcon
                className="stop__close-icon-v2"
                onClick={(e): void => {
                  e.stopPropagation();
                  tripToDelete(segment.ui_id);
                  handleOpenDeleteTripConfirmation(true);
                }}
              />
            )}
          </Stack>
        </TwAccordionSummary>
        <TwAccordionDetails>
          <DragDropContext onDragEnd={onDragEnd}>
            {enabled && (
              <Droppable droppableId={`droppable-trip-${segment.ui_id}`} key={segment.ui_id}>
                {(droppableProvided, snapshot): JSX.Element => (
                  <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                    {segment.provider_stops.map((stop, stopIndex) => (
                      <>
                        {segmentIndex > 0 && stopIndex === 0 ? (
                          <TwOnlyinfoStop
                            stop={stop}
                            ref={(element): void => {
                              stopsRef.current[stopIndex] = element;
                            }}
                            isImport={isImport}
                            hasLocationBeenUpdated={hasLocationBeenUpdated}
                            isCreateOrder={createOrder}
                          />
                        ) : (
                          <TwStop
                            key={`route-info-stop-${stop.ui_id}`}
                            ref={(element): void => {
                              stopsRef.current[stopIndex] = element;
                            }}
                            stIndex={stopIndex}
                            segmentId={segment.ui_id}
                            segmentOrder={segment.order}
                            stopsTotal={segment.provider_stops.length}
                            stop={stop}
                            locations={locations}
                            times={times}
                            isFullscreenImportLoad={fullScreenImport}
                            isImportLoad={isImport}
                            onCreateLocationClick={handleOpenCreateLocation}
                            onEditLocationClick={handleOpenEditLocation}
                            onLocationInputChange={handleStopLocationInputChange}
                            onLocationSelected={handleStopLocationSelected}
                            hasLocationBeenUpdated={hasLocationBeenUpdated}
                            isCreateOrder={createOrder}
                            entity={EntityType.ORDER}
                          />
                        )}
                      </>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            )}
          </DragDropContext>
        </TwAccordionDetails>
      </TwAccordion>
      {isSplit && segmentIndex === segments.length - 1 && (
        <TwButton
          label="Add trip"
          fullwidth
          disabled={segments.length === 10}
          action={addSegmentToRoute}
          type={TwButtonType.secondary}
          sx={{ margin: '10px 0' }}
        />
      )}
    </>
  );
};

export default TripsRoute;
